<template>
  <div class="ss-root" :class="{ 'ss-open': isOpen, 'ss-has-value': !!modelValue }" ref="root">
    <input
      ref="input"
      type="text"
      class="ss-input"
      :value="displayText"
      :placeholder="placeholder"
      autocomplete="off"
      spellcheck="false"
      @focus="onFocus"
      @input="onInput"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.escape.prevent="close"
    />
    <button
      v-if="clearable && modelValue"
      type="button"
      class="ss-clear"
      aria-label="Clear selection"
      @mousedown.prevent="onClear"
    >×</button>
    <svg v-else class="ss-caret" width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>

    <ul v-if="isOpen" class="ss-list" @mousedown.prevent ref="list">
      <li v-if="optionItems.length === 0" class="ss-empty">{{ emptyMessage }}</li>
      <template v-for="(item, i) in displayItems" :key="i">
        <li v-if="item.type === 'header'" class="ss-group-header">{{ item.label }}</li>
        <li
          v-else
          class="ss-option"
          :class="{ 'is-active': item.optionIndex === highlightedIndex, 'is-selected': item.opt.value === modelValue }"
          @click="select(item.opt)"
          @mouseenter="highlightedIndex = item.optionIndex"
        >
          <span class="ss-option-label">{{ item.opt.label }}</span>
          <span v-if="item.opt.hint" class="ss-option-hint">{{ item.opt.hint }}</span>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SearchableSelect",
  props: {
    modelValue: { type: [String, Number], default: "" },
    options: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Select…" },
    clearable: { type: Boolean, default: true },
    minCharsForSearch: { type: Number, default: 0 },
    maxVisibleOptions: { type: Number, default: 120 },
  },
  emits: ["update:modelValue", "change", "focus"],
  data() {
    return {
      isOpen: false,
      query: "",
      highlightedIndex: 0,
    };
  },
  computed: {
    hasGroups() { return this.groups.length > 0; },
    normalizedQuery() {
      return this.query.trim().toLowerCase();
    },
    effectiveLimit() {
      const n = Number(this.maxVisibleOptions);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : 120;
    },
    flatOptions() {
      return this.hasGroups
        ? this.groups.flatMap((g) => g.options || [])
        : this.options;
    },
    normalizedOptions() {
      return this.options.map((o) => ({
        ...o,
        _ssLabelLc: String(o?.label || "").toLowerCase(),
        _ssValueLc: String(o?.value || "").toLowerCase(),
      }));
    },
    normalizedGroups() {
      return this.groups.map((g) => ({
        label: g.label,
        options: (g.options || []).map((o) => ({
          ...o,
          _ssLabelLc: String(o?.label || "").toLowerCase(),
          _ssValueLc: String(o?.value || "").toLowerCase(),
        })),
      }));
    },
    selectedOption() {
      return this.flatOptions.find((o) => o.value === this.modelValue) || null;
    },
    displayText() {
      if (this.isOpen) return this.query;
      return this.selectedOption ? this.selectedOption.label : "";
    },
    displayItems() {
      const q = this.normalizedQuery;
      if (q.length < this.minCharsForSearch) return [];
      const matches = (o) =>
        !q ||
        o._ssLabelLc.includes(q) ||
        o._ssValueLc.includes(q);

      const items = [];
      let optionIndex = 0;
      let count = 0;
      const limit = this.effectiveLimit;
      if (this.hasGroups) {
        for (const g of this.normalizedGroups) {
          let groupHasMatches = false;
          for (const opt of g.options) {
            if (!matches(opt)) continue;
            if (!groupHasMatches) {
              items.push({ type: "header", label: g.label });
              groupHasMatches = true;
            }
            items.push({ type: "option", opt, optionIndex: optionIndex++ });
            count += 1;
            if (count >= limit) return items;
          }
        }
      } else {
        for (const opt of this.normalizedOptions) {
          if (!matches(opt)) continue;
          items.push({ type: "option", opt, optionIndex: optionIndex++ });
          count += 1;
          if (count >= limit) return items;
        }
      }
      return items;
    },
    optionItems() {
      return this.displayItems.filter((i) => i.type === "option");
    },
    emptyMessage() {
      if (this.normalizedQuery.length < this.minCharsForSearch) {
        const n = this.minCharsForSearch;
        return `Type at least ${n} character${n === 1 ? "" : "s"} to search`;
      }
      return "No matches";
    },
  },
  watch: {
    optionItems() {
      if (this.highlightedIndex >= this.optionItems.length) {
        this.highlightedIndex = 0;
      }
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onDocMouseDown);
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.onDocMouseDown);
  },
  methods: {
    onFocus() {
      this.query = "";
      this.isOpen = true;
      const idx = this.optionItems.findIndex((i) => i.opt.value === this.modelValue);
      this.highlightedIndex = idx >= 0 ? idx : 0;
      this.$emit("focus");
    },
    onInput(e) {
      this.query = e.target.value;
      this.isOpen = true;
      this.highlightedIndex = 0;
    },
    moveHighlight(delta) {
      if (!this.isOpen) { this.isOpen = true; return; }
      const len = this.optionItems.length;
      if (!len) return;
      this.highlightedIndex = (this.highlightedIndex + delta + len) % len;
      this.$nextTick(() => this.scrollHighlightedIntoView());
    },
    scrollHighlightedIntoView() {
      const list = this.$refs.list;
      if (!list) return;
      const target = list.querySelector(".ss-option.is-active");
      if (target && target.scrollIntoView) target.scrollIntoView({ block: "nearest" });
    },
    selectHighlighted() {
      const item = this.optionItems[this.highlightedIndex];
      if (item) this.select(item.opt);
    },
    select(opt) {
      this.$emit("update:modelValue", opt.value);
      this.$emit("change", opt.value);
      this.isOpen = false;
      this.query = "";
      this.$refs.input?.blur();
    },
    onClear() {
      this.$emit("update:modelValue", "");
      this.$emit("change", "");
      this.query = "";
      this.isOpen = false;
    },
    close() {
      this.isOpen = false;
      this.query = "";
      this.$refs.input?.blur();
    },
    onDocMouseDown(e) {
      if (!this.$refs.root) return;
      if (!this.$refs.root.contains(e.target)) this.close();
    },
  },
};
</script>

<style scoped>
/* Colours, radii and control height come from CSS variables with the public
   site's own values as the fallbacks: inside .admin-page the --g-* tokens
   resolve and this matches the console theme, everywhere else nothing
   changes. Keep it that way — no hard-coded palette literals below. */
.ss-root {
  position: relative;
  width: 100%;
}
.ss-input {
  width: 100%;
  height: var(--g-control-h, 44px);
  padding: 0 38px 0 14px;
  border: 1px solid var(--g-border-strong, #e2e8f0);
  border-radius: var(--g-radius, 12px);
  background: var(--g-surface, #fff);
  font-size: var(--g-control-fs, 14px);
  color: var(--g-text, #0f172a);
  font-weight: 500;
  box-shadow: var(--g-shadow-2, 0 1px 2px rgba(15, 23, 42, 0.04));
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background-color 0.15s;
}
.ss-input::placeholder { color: var(--g-text-disabled, #94a3b8); font-weight: 400; }
.ss-input:hover { border-color: var(--g-border-hover, #cbd5e1); background-color: var(--g-hover, #f8fafc); }
.ss-input:focus,
.ss-open .ss-input {
  outline: none;
  cursor: text;
  border-color: var(--g-blue, #3467d5);
  background: var(--g-surface, #fff);
  box-shadow: var(--g-focus-ring, 0 0 0 3px rgba(52, 103, 213, 0.15));
}
.ss-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--g-text-secondary, #64748b);
  pointer-events: none;
  transition: transform 0.15s;
}
.ss-open .ss-caret { transform: translateY(-50%) rotate(180deg); }
.ss-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--g-text-secondary, #64748b);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.ss-clear:hover { background: var(--g-hover, #f1f5f9); color: var(--g-text, #0f172a); }

.ss-list {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 320px;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--g-surface, #fff);
  border: 1px solid var(--g-border, #e2e8f0);
  border-radius: var(--g-radius, 12px);
  box-shadow: var(--g-shadow-3, 0 8px 28px rgba(15, 23, 42, 0.12));
  z-index: 100;
}
.ss-group-header {
  position: sticky;
  top: 0;
  margin: 4px -6px 4px;
  padding: 8px 18px 6px;
  font-size: 11px;
  font-weight: 700;
  color: var(--g-text-secondary, #1f3987);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: var(--g-group-head, linear-gradient(180deg, #f1f5fb 0%, #f8fafc 100%));
  border-top: 1px solid var(--g-border, #e2e8f0);
  border-bottom: 1px solid var(--g-border, #e2e8f0);
  user-select: none;
  z-index: 1;
}
.ss-group-header:first-child { border-top: none; margin-top: -6px; border-top-left-radius: 6px; border-top-right-radius: 6px; }
.ss-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 12px;
  font-size: var(--g-control-fs, 14px);
  color: var(--g-text, #0f172a);
  border-radius: var(--g-radius-sm, 8px);
  cursor: pointer;
  user-select: none;
}
.ss-option.is-active { background: var(--g-blue-tint, #eff4ff); color: var(--g-blue, #1f3987); }
.ss-option.is-selected { background: var(--g-blue-tint-2, #dbe6ff); color: var(--g-blue, #1f3987); font-weight: 600; }
.ss-option-hint {
  color: var(--g-text-disabled, #94a3b8);
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}
.ss-empty {
  padding: 10px 12px;
  color: var(--g-text-secondary, #64748b);
  font-size: var(--g-control-fs, 14px);
  text-align: center;
}
</style>
